import type { ChatDoc } from '@odo/shared-ui';
import {
    MAX_QUICK_REPLY_BODY_DOC_JSON_LENGTH,
    QUICK_REPLY_CODE_PATTERN,
    type ConversationQuickReply,
    type CreateQuickReplyBody,
    type QuickReplyChatType
} from '../../../services/conversationQuickReplies';
import { quickReplyDocText } from '../chat/quickReplyDocText';

export interface QuickReplyFormValues {
    code: string;
    title: string;
    category: string;
    chatType: QuickReplyChatType;
    bodyDoc: ChatDoc;
    sortOrder: string;
}

// Digits only, and short enough to stay inside the backend's int32.
const SORT_ORDER_PATTERN = /^\d{1,9}$/;
const EMPTY_DOC: ChatDoc = { type: 'doc', content: [{ type: 'paragraph' }] };

export function initialFormValues(reply: ConversationQuickReply | null): QuickReplyFormValues {
    if (!reply) {
        return {
            code: '',
            title: '',
            category: '',
            chatType: 'external',
            bodyDoc: EMPTY_DOC,
            sortOrder: ''
        };
    }
    const { code, title, category, bodyDoc } = reply;
    return {
        code,
        title,
        category,
        chatType: reply.chatType,
        bodyDoc,
        sortOrder: String(reply.sortOrder)
    };
}

export function isCodeValid(code: string): boolean {
    return QUICK_REPLY_CODE_PATTERN.test(code.trim());
}

export function isSortOrderValid(sortOrder: string): boolean {
    return sortOrder.trim() === '' || SORT_ORDER_PATTERN.test(sortOrder.trim());
}

export function isBodyTooLong(bodyDoc: ChatDoc): boolean {
    return JSON.stringify(bodyDoc).length > MAX_QUICK_REPLY_BODY_DOC_JSON_LENGTH;
}

export function isFormComplete(values: QuickReplyFormValues): boolean {
    return (
        isCodeValid(values.code) &&
        isSortOrderValid(values.sortOrder) &&
        [values.title, values.category, quickReplyDocText(values.bodyDoc)].every(
            (field) => field.trim() !== ''
        ) &&
        !isBodyTooLong(values.bodyDoc)
    );
}

export function toRequestBody(values: QuickReplyFormValues): CreateQuickReplyBody {
    const sortOrder = values.sortOrder.trim();
    return {
        code: values.code.trim(),
        title: values.title.trim(),
        category: values.category.trim(),
        chatType: values.chatType,
        bodyDoc: values.bodyDoc,
        ...(sortOrder !== '' && { sortOrder: Number(sortOrder) })
    };
}
